import { useState } from "react";
import { toast } from "sonner";
import { api, auDate, errMsg } from "../lib/api";
import { Input } from "./ui/input";
import { useGet } from "../lib/hooks";
import { Pill } from "./Common";
import { Button } from "./ui/button";

const mb = (b) => (b >= 1073741824 ? `${(b / 1073741824).toFixed(2)} GB` : `${(b / 1048576).toFixed(1)} MB`);

export function StorageUsage() {
  const [d] = useGet("/agency/storage");
  if (!d) return null;
  const pct = (r) => (r.limit_gb ? Math.min(100, (r.total_bytes / (r.limit_gb * 1024 ** 3)) * 100) : 0);
  return (
    <div className="space-y-4" data-testid="storage-usage">
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="rounded-md border bg-card p-4"><div className="overline">Total used</div><div className="mt-1 text-2xl font-bold text-[#C2A462]" data-testid="storage-total">{mb(d.total_bytes)}</div></div>
        <div className="rounded-md border bg-card p-4"><div className="overline">Documents & media stored in</div><div className="mt-1 text-sm">{d.documents_region}</div></div>
        <div className="rounded-md border bg-card p-4"><div className="overline">Backups stored in</div><div className="mt-1 text-sm">{d.backups_region}</div></div>
      </div>
      <div className="rounded-md border bg-card divide-y divide-white/5">
        {d.rows.map((r) => (
          <div key={r.account_id || "ent"} className="grid items-center gap-3 p-3 sm:grid-cols-[220px_1fr_200px]" data-testid={`storage-row-${r.account_id || "enterprise"}`}>
            <div className="truncate text-sm font-medium">{r.name}</div>
            <div className="h-2 rounded-full bg-white/5"><div className={`h-2 rounded-full ${pct(r) >= 90 ? "bg-red-500" : pct(r) >= 80 ? "bg-amber-400" : "bg-[#C2A462]"}`} style={{ width: `${Math.max(1, pct(r))}%` }} /></div>
            <div className="text-right text-xs text-muted-foreground" data-testid={`storage-cap-${r.account_id || "enterprise"}`}><span className="text-foreground">{mb(r.total_bytes)}{r.limit_gb ? ` of ${r.limit_gb} GB · ${pct(r).toFixed(1)}%` : ""}</span><div>{r.media_files} media · {r.pdf_files} form PDFs</div></div>
          </div>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">Counts media library files and saved form PDFs. Database records and backups aren't included.</p>
    </div>
  );
}

const Stat = ({ label, value, tid }) => <div className="rounded-md border bg-card p-4"><div className="overline">{label}</div><div className="mt-1 text-2xl font-bold" data-testid={tid}>{value}</div></div>;

function BackupAlert() {
  const [d, reload] = useGet("/agency/backup-alert");
  const [v, setV] = useState(null);
  if (!d) return null;
  const val = v ?? d.email;
  const save = async () => { try { await api.put("/agency/backup-alert", { email: val }); toast.success("Saved"); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="rounded-md border bg-card p-5 space-y-2" data-testid="backup-alert-card">
      <div className="overline">Backup failure alerts</div>
      <p className="text-sm text-muted-foreground">A full backup runs every night at 2am (Melbourne). If it fails, an email goes to this address.</p>
      <div className="flex gap-2"><Input value={val} onChange={(e) => setV(e.target.value)} placeholder="you@business.com.au" data-testid="backup-alert-email" /><Button onClick={save} data-testid="backup-alert-save">Save</Button></div>
    </div>
  );
}

export function SecurityOverview() {
  const [acc, setAcc] = useState("");
  const [page, setPage] = useState(1);
  const [accounts] = useGet("/accounts");
  const [d] = useGet(`/agency/security?account_id=${acc}&page=${page}`, [acc, page]);
  if (!d) return null;
  return (
    <div className="space-y-5" data-testid="security-overview">
      <label className="flex items-center gap-2 text-sm text-muted-foreground">Show
        <select className="h-9 rounded-md border border-input bg-background px-2 text-sm text-foreground" value={acc} onChange={(e) => { setAcc(e.target.value); setPage(1); }} data-testid="security-entity-filter">
          <option value="">All entities</option>{(accounts || []).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select></label>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Stat label="Active users" value={d.users_total} tid="sec-users" />
        <Stat label="Sign-ins (30 days)" value={d.logins_30d} tid="sec-logins" />
        <Stat label="Locked after failed attempts" value={d.locked.length} tid="sec-locked" />
        <Stat label="Last successful backup" value={d.last_backup ? auDate(d.last_backup) : "None yet"} tid="sec-backup" />
      </div>
      <BackupAlert />
      <div className="rounded-md border bg-card p-5 space-y-2">
        <div className="overline">Two-step sign-in</div>
        <p className="text-sm text-muted-foreground">Everyone signs in with a password plus a one-time code. Users without a mobile number can only get the code by email. Adding a mobile gives them an SMS option once SMS is switched on.</p>
        {d.email_only.length ? <div className="flex flex-wrap gap-2">{d.email_only.map((u) => <Pill key={u.email} tone="amber">{u.name || u.email} · {u.entity}</Pill>)}</div> : <Pill tone="green">Every user has a mobile number</Pill>}
      </div>
      {d.locked.length > 0 && <div className="rounded-md border border-red-500/30 bg-card p-5 text-sm" data-testid="sec-locked-list">{d.locked.map((l) => <div key={l.identifier}>{l.identifier} · {l.count} failed attempts · {l.last?.slice(0, 16).replace("T", " ")}</div>)}</div>}
      <div className="rounded-md border bg-card p-5">
        <div className="overline mb-2">Recent sign-ins</div>
        <div className="divide-y divide-white/5 text-sm" data-testid="sec-recent-logins">
          {d.recent_logins.map((l) => <div key={l.at + l.email} className="flex flex-wrap justify-between gap-2 py-2"><span>{l.email} <span className="text-muted-foreground">· {l.entity}</span></span><span className="text-xs text-muted-foreground">{l.detail} · {l.at.slice(0, 16).replace("T", " ")}</span></div>)}
          {!d.recent_logins.length && <div className="py-2 text-muted-foreground">No sign-ins recorded yet.</div>}
        </div>
        {d.pages > 1 && <div className="mt-3 flex items-center justify-end gap-2 text-xs"><Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="sec-prev">Previous</Button>Page {d.page} of {d.pages}<Button size="sm" variant="ghost" disabled={page >= d.pages} onClick={() => setPage(page + 1)} data-testid="sec-next">Next</Button></div>}
      </div>
      <p className="text-xs text-muted-foreground">{d.changes_30d} changes were recorded in the audit log in the last 30 days.</p>
    </div>
  );
}
